
const libPictProvider = require('pict-provider');

/**
 * Data provider for platform dashboard views.
 *
 * Centralizes API calls to the Platform Data Operations service,
 * Chart.js instance management, and common formatting utilities.
 */
class PlatformDashboardDataProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;

		this._Charts = {};
		this._RNIMap = null;
	}

	/**
	 * Get the customer ID for the current session.
	 * @return {number|null}
	 */
	getCustomerID()
	{
		return this.pict.AppData.CurrentUser?.CustomerID || null;
	}

	/**
	 * Fetch dashboard data from the Platform Data Operations service.
	 *
	 * @param {string} pEndpoint - The endpoint path (after /1.0/).
	 * @return {Promise<any>}
	 */
	async fetchData(pEndpoint)
	{
		return new Promise((resolve, reject) =>
		{
			const restClient = this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
			if (!restClient)
			{
				this.pict.log.warn('HeadlightRestClient not available for dashboard data');
				resolve(null);
				return;
			}

			restClient.getJSON(pEndpoint, (pError, pBody, pRawResponse) =>
			{
				if (pError)
				{
					this.pict.log.warn(`Dashboard fetch error for ${pEndpoint}: ${pError}`);
					resolve(null);
					return;
				}
				this.pict.log.info(`Dashboard data for ${pEndpoint}: type=${typeof pBody}, isArray=${Array.isArray(pBody)}, keys=${pBody ? Object.keys(pBody).slice(0, 5).join(',') : 'null'}`);
				resolve(pBody);
			});
		});
	}

	/**
	 * Page through a list endpoint that returns `{total, items}` envelopes
	 * (or a bare array — both shapes are tolerated) and concatenate the
	 * results.  Used by panels that need the *whole* customer set rather
	 * than the first server page, e.g. the Users panel computing KPIs.
	 *
	 * Bounded by `maxPages` so a runaway customer doesn't drag the browser
	 * down; defaults match the service's `_pagingFromQuery` max cap of 200.
	 *
	 * @param {string} pEndpoint - Endpoint path (no query string).
	 * @param {object} [pOptions]
	 * @param {number} [pOptions.pageSize=200]
	 * @param {number} [pOptions.maxPages=100]
	 * @return {Promise<Array<object>>}
	 */
	async fetchAllPages(pEndpoint, pOptions)
	{
		const tmpPageSize = (pOptions && pOptions.pageSize) || 200;
		const tmpMaxPages = (pOptions && pOptions.maxPages) || 100;
		const tmpJoin = pEndpoint.indexOf('?') === -1 ? '?' : '&';
		const tmpAll = [];
		for (let i = 0; i < tmpMaxPages; i++)
		{
			const tmpOffset = i * tmpPageSize;
			const tmpURL = `${pEndpoint}${tmpJoin}offset=${tmpOffset}&limit=${tmpPageSize}`;
			const tmpData = await this.fetchData(tmpURL);
			const tmpItems = Array.isArray(tmpData)
				? tmpData
				: (tmpData && Array.isArray(tmpData.items) ? tmpData.items : []);
			if (tmpItems.length === 0)
			{
				break;
			}
			tmpAll.push(...tmpItems);
			if (tmpItems.length < tmpPageSize)
			{
				break;
			}
			if (i === tmpMaxPages - 1)
			{
				this.pict.log.warn(
					`fetchAllPages(${pEndpoint}): hit ${tmpMaxPages}-page ceiling (${tmpAll.length} rows) — caller should narrow`);
			}
		}
		return tmpAll;
	}

	/**
	 * Get the ReportNamedInstance hash→name mapping (cached).
	 * @return {Promise<object>}
	 */
	async getRNIMap()
	{
		if (this._RNIMap)
		{
			return this._RNIMap;
		}
		const data = await this.fetchData('PlatformDashboard/RNIMap');
		this._RNIMap = data || {};
		return this._RNIMap;
	}

	/**
	 * Resolve a DocumentType hash to a friendly name.
	 * @param {string} pHash
	 * @return {string}
	 */
	rniName(pHash)
	{
		if (!pHash)
		{
			return pHash;
		}
		return (this._RNIMap && this._RNIMap[pHash]) || pHash;
	}

	/**
	 * Destroy a chart by container ID, if it exists.
	 * @param {string} pChartID
	 */
	destroyChart(pChartID)
	{
		if (this._Charts[pChartID])
		{
			this._Charts[pChartID].destroy();
			delete this._Charts[pChartID];
		}
	}

	/**
	 * Create or replace a Chart.js chart.
	 *
	 * @param {string} pCanvasID - The canvas element ID.
	 * @param {string} pType - Chart type (line, bar, doughnut, etc.).
	 * @param {object} pData - Chart.js data config.
	 * @param {object} [pOptions] - Chart.js options.
	 * @return {object|null} The Chart instance, or null if canvas not found.
	 */
	createChart(pCanvasID, pType, pData, pOptions)
	{
		this.destroyChart(pCanvasID);

		const canvas = document.getElementById(pCanvasID);
		if (!canvas)
		{
			return null;
		}

		const Chart = window.Chart;
		if (!Chart)
		{
			this.pict.log.warn('Chart.js not loaded');
			return null;
		}

		const chart = new Chart(canvas.getContext('2d'),
			{
				type: pType,
				data: pData,
				options: Object.assign(
					{
						responsive: true,
						maintainAspectRatio: false,
						plugins:
						{
							legend: { position: 'bottom' },
						},
					}, pOptions || {}),
			});

		this._Charts[pCanvasID] = chart;
		return chart;
	}

	/**
	 * Standard color palette for charts.
	 * @return {string[]}
	 */
	get chartColors()
	{
		return [
			'#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6',
			'#ec4899', '#64748b', '#14b8a6', '#f97316', '#06b6d4',
			'#84cc16', '#a855f7', '#e11d48', '#0ea5e9', '#eab308',
		];
	}

	/**
	 * Format a number with commas.
	 * @param {number} pNum
	 * @return {string}
	 */
	formatNumber(pNum)
	{
		if (pNum == null || isNaN(pNum))
		{
			return '--';
		}
		return Number(pNum).toLocaleString();
	}

	/**
	 * Format a date string for display.
	 * @param {string} pDate
	 * @return {string}
	 */
	formatDate(pDate)
	{
		if (!pDate)
		{
			return '--';
		}
		try
		{
			return new Date(pDate).toLocaleDateString();
		}
		catch (e)
		{
			return pDate;
		}
	}

	/**
	 * Format a currency amount.
	 * @param {number} pAmount
	 * @return {string}
	 */
	formatMoney(pAmount)
	{
		if (pAmount == null || isNaN(pAmount))
		{
			return '--';
		}
		return '$' + Number(pAmount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
	}

	/**
	 * Destroy all tracked charts (for cleanup on view tear-down).
	 */
	destroyAllCharts()
	{
		for (const chartID of Object.keys(this._Charts))
		{
			this.destroyChart(chartID);
		}
	}
}

module.exports = PlatformDashboardDataProvider;

module.exports.default_configuration =
{
	ProviderIdentifier: 'PlatformDashboardData',
	AutoInitialize: true,
	AutoInitializeOrdinal: 0,
};
